import { Check } from "lucide-react";
import type { ReactNode } from "react";

interface RoleCardProps {
  icon: ReactNode;
  title: string;
  description: string;
  features: string[];
  isSelected: boolean;
  onClick: () => void;
}

export const RoleCard = ({
  icon,
  title,
  description,
  features,
  isSelected,
  onClick,
}: RoleCardProps) => {
  return (
    <button
      type="button"
      className="group relative flex h-full w-full flex-col
        transition-all duration-300 ease-out
        cursor-pointer rounded-2xl
        bg-background
        shadow-[var(--shadow-depth-sm)]
        ring-1 ring-inset ring-gray-300/40
        hover:shadow-[var(--shadow-depth-md)]
        hover:border-primary/40
        aria-checked:ring-2 aria-checked:ring-primary aria-checked:ring-offset-2
        aria-checked:shadow-[var(--shadow-depth-lg)]
        text-left
        before:absolute before:inset-x-0 before:top-0 before:h-px
        before:bg-gradient-to-r before:from-transparent before:via-white/50 before:to-transparent
        before:rounded-t-2xl"
      role="radio"
      aria-checked={isSelected}
      onClick={onClick}
    >
      <div className="flex h-full flex-col gap-3 sm:gap-6 p-4 sm:p-8">
        {/* Header */}
        <div className="flex items-start justify-between gap-2 sm:gap-3 md:min-h-[4.5rem]">
          <div className="flex min-w-0 items-start gap-3">
            <div
              className="flex-shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-xl
              bg-accent
              text-brand-ink
              flex items-center justify-center
              ring-1 ring-gray-300/40"
            >
              {icon}
            </div>
            <div className="flex min-w-0 flex-col gap-0.5 sm:gap-1">
              <h2 className="text-balance text-base font-semibold leading-snug text-gray-900 sm:text-lg">
                {title}
              </h2>
              <p className="text-xs sm:text-sm text-gray-600">{description}</p>
            </div>
          </div>

          {/* Selection indicator */}
          <div
            className={`flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full transition-all duration-200
              ${
                isSelected
                  ? "bg-primary text-primary-foreground scale-100"
                  : "bg-bg text-transparent scale-95 group-hover:scale-100 group-hover:bg-bg-dark"
              }
              flex items-center justify-center`}
          >
            <Check className="w-3 h-3 sm:w-4 sm:h-4" strokeWidth={3} />
          </div>
        </div>

        {/* Features - all visible but more compact on mobile */}
        <ul className="mt-auto flex flex-col gap-1.5 sm:gap-3">
          {features.map((feature, index) => (
            <li
              key={index}
              className="flex items-start gap-2 sm:gap-3 text-xs sm:text-sm text-gray-700"
            >
              <Check
                className="w-3 h-3 sm:w-4 sm:h-4 mt-0.5 flex-shrink-0 text-brand-ink"
                strokeWidth={2.5}
              />
              <span className="leading-relaxed sm:leading-normal">
                {feature}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </button>
  );
};
